import { useEffect, useMemo, useRef } from 'react';
import { nip19 } from 'nostr-tools';
import { useParams, Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { useAuthor } from '@/hooks/useAuthor';
import { useMyPosts } from '@/hooks/useMyPosts';
import { ProfileHeader } from '@/components/ProfileHeader';
import { RetroNoteCard } from '@/components/RetroNoteCard';
import { RetroNoteSkeleton } from '@/components/RetroNoteSkeleton';
import { LoginArea } from '@/components/auth/LoginArea';
import { genUserName } from '@/lib/genUserName';
import NotFound from './NotFound';

export function NIP19Page() {
  const { nip19: identifier } = useParams<{ nip19: string }>();

  if (!identifier) return <NotFound />;

  let decoded: ReturnType<typeof nip19.decode>;
  try {
    decoded = nip19.decode(identifier);
  } catch {
    return <NotFound />;
  }

  switch (decoded.type) {
    case 'npub':
      return <ProfileView pubkey={decoded.data} />;
    case 'nprofile':
      return <ProfileView pubkey={decoded.data.pubkey} />;
    case 'note':
      return <NoteView eventId={decoded.data} />;
    case 'nevent':
      return <NoteView eventId={decoded.data.id} author={decoded.data.author} />;
    default:
      return <NotFound />;
  }
}

function TopBar() {
  return (
    <div className="sticky top-0 z-30 border-b-2 border-primary/70 bg-black/80 backdrop-blur-md">
      <div className="max-w-3xl mx-auto px-3 md:px-4 py-2 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2 min-w-0 hover:opacity-80">
          <span className="font-pixel text-neon-green text-glow-green animate-star-twinkle">★</span>
          <span className="font-pixel text-sm md:text-base text-neon-magenta text-glow-magenta tracking-widest truncate">
            PROFILE.EXE
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <Link
            to="/"
            className="pixel-border-soft px-2 py-1 font-pixel text-[10px] text-neon-cyan hover:text-glow-cyan tracking-widest"
          >
            [HOME]
          </Link>
          <LoginArea className="shrink-0" />
        </div>
      </div>
    </div>
  );
}

function ProfileView({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useMyPosts(pubkey);

  const events = useMemo(
    () => data?.pages.flatMap((p) => p.events) ?? [],
    [data],
  );

  const displayName = metadata?.display_name ?? metadata?.name ?? genUserName(pubkey);

  useSeoMeta({
    title: `${displayName} // PROFILE.EXE`,
    description: metadata?.about ?? 'A retro 90s-themed Nostr profile.',
  });

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage || isFetchingNextPage) return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) fetchNextPage(); },
      { rootMargin: '400px 0px' },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, events.length]);

  return (
    <main className="relative min-h-screen">
      <TopBar />
      <div className="max-w-3xl mx-auto px-3 md:px-4 pt-4 md:pt-6 pb-24 space-y-6">
        <ProfileHeader
          pubkey={pubkey}
          metadata={metadata}
          postCount={events.length}
          isLoading={isLoading}
        />

        <div className="flex items-center justify-between">
          <h2 className="font-pixel text-lg md:text-xl text-neon-cyan text-glow-cyan tracking-widest blink-cursor">
            &gt; TRANSMISSIONS
          </h2>
          <div className="font-pixel text-[10px] text-muted-foreground tracking-widest">
            {isLoading ? 'SCANNING…' : `LOADED:${String(events.length).padStart(3, '0')}`}
          </div>
        </div>

        {isLoading && (
          <div className="space-y-4">
            <RetroNoteSkeleton />
            <RetroNoteSkeleton />
          </div>
        )}

        {!isLoading && events.length === 0 && (
          <div className="pixel-border-soft bg-black/60 p-8 text-center">
            <div className="font-pixel text-sm text-neon-cyan tracking-widest">NO TRANSMISSIONS DETECTED</div>
          </div>
        )}

        <div className="space-y-4">
          {events.map((event, i) => (
            <RetroNoteCard key={event.id} event={event} index={i} />
          ))}
        </div>

        {events.length > 0 && (
          <div className="pt-4 text-center">
            {isFetchingNextPage ? (
              <div className="font-pixel text-xs text-neon-amber text-glow-amber animate-pulse tracking-widest">
                LOADING NEXT LEVEL…
              </div>
            ) : hasNextPage ? (
              <button
                onClick={() => fetchNextPage()}
                className="pixel-border-soft bg-black/80 px-5 py-2 font-pixel text-xs text-neon-cyan hover:text-glow-cyan tracking-widest"
              >
                &gt; PRESS START TO CONTINUE
              </button>
            ) : (
              <div className="font-pixel text-xs text-muted-foreground tracking-widest">
                ★ GAME OVER — NO MORE POSTS ★
              </div>
            )}
            <div ref={sentinelRef} className="h-1 w-full" aria-hidden />
          </div>
        )}
      </div>
    </main>
  );
}

function useSingleEvent(eventId: string, author?: string) {
  const { nostr } = useNostr();
  return useQuery<NostrEvent | null>({
    queryKey: ['single-event', eventId, author ?? ''],
    queryFn: async ({ signal }) => {
      const filter = author
        ? { ids: [eventId], authors: [author], limit: 1 }
        : { ids: [eventId], limit: 1 };
      const [event] = await nostr.query([filter], {
        signal: AbortSignal.any([signal, AbortSignal.timeout(6000)]),
      });
      return event ?? null;
    },
    staleTime: 5 * 60 * 1000,
  });
}

function NoteView({ eventId, author }: { eventId: string; author?: string }) {
  const { data: event, isLoading, isError } = useSingleEvent(eventId, author);

  useSeoMeta({
    title: 'TRANSMISSION // PROFILE.EXE',
    description: 'A Nostr transmission displayed on a retro terminal.',
  });

  return (
    <main className="relative min-h-screen">
      <TopBar />
      <div className="max-w-3xl mx-auto px-3 md:px-4 pt-4 md:pt-6 pb-24 space-y-4">
        <h1 className="font-pixel text-xl md:text-2xl text-neon-magenta text-glow-magenta tracking-widest blink-cursor">
          &gt; SINGLE TRANSMISSION
        </h1>

        {isLoading && <RetroNoteSkeleton />}

        {isError && (
          <div className="pixel-border bg-black/80 p-4 font-pixel text-neon-red text-sm">
            &gt; TRANSMISSION CORRUPTED
          </div>
        )}

        {!isLoading && !event && !isError && (
          <div className="pixel-border-soft bg-black/60 p-8 text-center font-pixel text-sm text-neon-cyan tracking-widest">
            TRANSMISSION NOT FOUND
          </div>
        )}

        {event && <RetroNoteCard event={event} />}
      </div>
    </main>
  );
}
